import React from "react";
import TableRow from "@mui/material/TableRow";
import { Button, IconButton, TableCell, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { InvoiceItem } from "@rentablo/api/apiv1";
import EyeIcon from "@icons/myicons/eye.svg?react";
import TaxIcon from "@icons/myicons/help-wheel.svg?react";
import { toCurrency } from "@helpers";
import { GainLossTypography } from "@rentablo/components";

const StyledCell = styled(TableCell)`
  padding: ${({ theme }) => theme.spacing(4, 0, 0)};
`;

const FirstCell = styled(StyledCell)`
  padding-left: 0;
  width: 40%;
`;

const LastCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(2)};
  align-self: flex-end;
`;

const CellChild = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
`;

const Controls = styled.div`
  gap: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing(8)};
`;

export interface CashbackSummaryRowDesktopProps {
  invoiceItem: InvoiceItem;
  billingCycleId?: number;
  showInvoice: (ids: number[]) => void;
  onTaxHelperOpen: (accountHolderId: number) => void;
}

export const CashbackSummaryRowDesktop = (
  props: CashbackSummaryRowDesktopProps
) => {
  const { invoiceItem, billingCycleId, showInvoice, onTaxHelperOpen } = props;
  const { t } = useTranslation();

  return (
    <TableRow>
      <FirstCell>
        <div>
          <div>
            <Typography variant="body4">
              {`${invoiceItem.accountHolderReference!.firstName} ${
                invoiceItem.accountHolderReference!.lastName
              }`}
            </Typography>
          </div>
          <div>
            <Typography variant="body3">{`${toCurrency(
              invoiceItem.totalPortfolioCommission
            )}
                      / ${toCurrency(invoiceItem.totalAgio)}`}</Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              fontWeight="600"
            >
              {t("domains.cashback.dashboard.summary.trailerCommission")}
            </Typography>
          </div>
        </div>
      </FirstCell>
      <StyledCell>
        <CellChild>
          {!billingCycleId && (
            <Typography variant="body3" color="text.tertiary">
              {t("domains.cashback.dashboard.summary.afterBilling")}
            </Typography>
          )}
          {billingCycleId && (
            <GainLossTypography
              value={invoiceItem.internalTransaction?.amount || 0}
            >
              {toCurrency(invoiceItem.internalTransaction?.amount || 0)}
            </GainLossTypography>
          )}

          <Typography
            variant="caption"
            color="textSecondary"
            component="p"
            fontWeight="600"
          >
            {t("domains.cashback.dashboard.summary.yourCashback")}
          </Typography>
        </CellChild>
      </StyledCell>
      <StyledCell>
        <CellChild>
          <LastCell>
            <div>
              {billingCycleId && (
                <Typography variant="body3">{`${toCurrency(
                  invoiceItem.cost
                )}`}</Typography>
              )}
              {!billingCycleId && (
                <Typography variant="body3" color="text.tertiary">
                  {t("domains.cashback.dashboard.summary.afterBilling")}
                </Typography>
              )}
              <Typography
                variant="caption"
                component="p"
                color="textSecondary"
                fontWeight="600"
              >
                {t("domains.cashback.dashboard.summary.commissionRetention")}
              </Typography>
            </div>
            <Controls>
              <IconButton
                size="small"
                disabled={!billingCycleId}
                onClick={() =>
                  showInvoice(invoiceItem.securityCommissionIds || [])
                }
                color="outlined"
              >
                <EyeIcon />
              </IconButton>
              {billingCycleId && (
                <Button
                  color="primary"
                  variant="text"
                  size="small"
                  startIcon={<TaxIcon width="16px" />}
                  onClick={() => {
                    if (!invoiceItem?.accountHolderReference?.id) return;
                    onTaxHelperOpen(invoiceItem?.accountHolderReference?.id);
                  }}
                >
                  {t("domains.cashback.dashboard.summary.taxHelper")}
                </Button>
              )}
            </Controls>
          </LastCell>
        </CellChild>
      </StyledCell>
    </TableRow>
  );
};
